import {
  MdAccessTime,
  MdCalendarToday,
  MdChevronLeft,
  MdChevronRight,
} from "react-icons/md";
import { AvailableSlotsDay } from "../utils/mockApi";
import { CalendarDay, DeliveryType, Order } from "../utils/type";

interface Props {
  setCurrentMonth: React.Dispatch<React.SetStateAction<Date>>;
  currentMonth: Date;
  calendarDays:  (CalendarDay | null)[];
  order: Order;
  setOrder: React.Dispatch<React.SetStateAction<Order>>;
  availableSlots?: AvailableSlotsDay[];
}

/** 當日已過時段：若 deliveryDate 為今天且 slot 的 end_time 已過則回傳 true（台灣本地時間） */
function isSlotPastForToday(deliveryDate: string, endTime: string): boolean {
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  if (deliveryDate !== todayStr) return false;
  const parts = endTime.split(":").map(Number);
  const slotEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), parts[0] ?? 0, parts[1] ?? 0, 0);
  return slotEnd <= now;
}

const DeliverTimeCard = ({
  setCurrentMonth,
  currentMonth,
  calendarDays,
  order,
  setOrder,
  availableSlots = [],
}: Props) => {
  const selectedDayData = order.deliveryDate
    ? availableSlots.find((d) => d.delivery_date === order.deliveryDate) ?? null
    : null;

  const changeMonth = (direction: number) => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
    setOrder((prev) => ({
      ...prev,
      deliveryDate: null,
      deliverySlotId: null,
      deliverySlotSource: null,
      deliverySlotLabel: null,
    }));
  };

  const handleDeliveryTypeChange = (type: DeliveryType) => {
    setOrder((prev) => ({
      ...prev,
      deliveryType: type,
      deliveryDate: null,
      deliverySlotId: null,
      deliverySlotSource: null,
      deliverySlotLabel: null,
    }));
    
    // 不預設「今天」，讓用戶根據 API 數據選擇
    // 如果切換到預約配送，保持當前的月份不變
  };
  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg">
      <div className="flex items-center space-x-3 mb-4">
        <div className="p-2.5 bg-blue-100 rounded-xl">
          <MdAccessTime className="text-blue-600 w-4 h-4" />
        </div>
        <h2 className="text-md font-bold text-gray-800">配送時間</h2>
      </div>

      {/* 配送方式選擇 */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-600 mb-3">
          配送方式
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => handleDeliveryTypeChange("scheduled")}
            className={`
                  p-4 rounded-xl transition-all duration-200 border-2
                  ${
                    order.deliveryType === "scheduled"
                      ? "bg-blue-500 border-blue-500 shadow-lg scale-105"
                      : "bg-white border-gray-200 hover:border-blue-300"
                  }
                `}
          >
            <div className="flex flex-col items-center space-y-2">
              <div
                className={`p-3 rounded-lg ${
                  order.deliveryType === "scheduled"
                    ? "bg-blue-400"
                    : "bg-blue-100"
                }`}
              >
                <MdCalendarToday
                  className={`w-6 h-6 ${
                    order.deliveryType === "scheduled"
                      ? "text-white"
                      : "text-blue-600"
                  }`}
                />
              </div>
              <span
                className={`font-semibold ${
                  order.deliveryType === "scheduled"
                    ? "text-white"
                    : "text-gray-800"
                }`}
              >
                預約配送
              </span>
            </div>
          </button>

          <button
            onClick={() => handleDeliveryTypeChange("immediate")}
            className={`
                  p-4 rounded-xl transition-all duration-200 border-2
                  ${
                    order.deliveryType === "immediate"
                      ? "bg-blue-500 border-blue-500 shadow-lg scale-105"
                      : "bg-white border-gray-200 hover:border-blue-300"
                  }
                `}
          >
            <div className="flex flex-col items-center space-y-2">
              <div
                className={`p-3 rounded-lg ${
                  order.deliveryType === "immediate"
                    ? "bg-blue-400"
                    : "bg-blue-100"
                }`}
              >
                <MdAccessTime
                  className={`w-6 h-6 ${
                    order.deliveryType === "immediate"
                      ? "text-white"
                      : "text-blue-600"
                  }`}
                />
              </div>
              <span
                className={`font-semibold ${
                  order.deliveryType === "immediate"
                    ? "text-white"
                    : "text-gray-800"
                }`}
              >
                立即配送
              </span>
            </div>
          </button>
        </div>
      </div>

      {/* 根據配送方式顯示不同內容 */}
      {order.deliveryType === "immediate" ? (
        // 立即配送內容
        <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
          <div className="text-center">
            <div className="inline-block p-4 bg-blue-100 rounded-full mb-4">
              <MdAccessTime className="w-10 h-10 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">立即配送</h3>
            <p className="text-gray-600 mb-4">
              我們收到您的需求後，會盡快安排配送人員為您服務
            </p>

            <div className="bg-white rounded-xl p-4 border border-blue-200">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">預計配送時間</span>
                <br />
                下單後將儘速為您安排配送人員
                <br />
                <span className="text-xs text-gray-500">
                  （視當前配送量而定）
                </span>
              </p>
            </div>
          </div>
        </div>
      ) : (
        // 預約配送內容
        <>
          {/* 日期選擇 */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-600 mb-3">
              選擇配送日期
            </label>

            {/* 月份切換 */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => changeMonth(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <MdChevronLeft className="w-5 h-5" />
              </button>
              <h4 className="text-base font-semibold">
                {currentMonth.getFullYear()}年 {currentMonth.getMonth() + 1}月
              </h4>
              <button
                onClick={() => changeMonth(1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <MdChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* 星期標題 */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {["日", "一", "二", "三", "四", "五", "六"].map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-semibold text-gray-600 py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* 日曆 */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((dayInfo, index) => {
                if (!dayInfo) {
                  return <div key={index} />;
                }

                const isSelected = order.deliveryDate === dayInfo.date;

                return (
                  <button
                    key={index}
                    onClick={() => {
                      if (dayInfo.isAvailable) {
                        setOrder((prev) => ({
                          ...prev,
                          deliveryDate: dayInfo.date,
                          deliverySlotId: null,
                          deliverySlotSource: null,
                          deliverySlotLabel: null,
                        }));
                      }
                    }}
                    disabled={!dayInfo.isAvailable}
                    className={`
                          aspect-square rounded-lg text-sm font-medium transition-all
                          ${
                            isSelected
                              ? "bg-blue-500 text-white shadow-lg scale-105"
                              : ""
                          }
                          ${
                            dayInfo.isAvailable && !isSelected
                              ? "bg-gray-50 text-gray-800 hover:bg-blue-100"
                              : ""
                          }
                          ${
                            !dayInfo.isAvailable
                              ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                              : ""
                          }
                        `}
                  >
                    <div className="flex flex-col items-center justify-center h-full">
                      <span>{dayInfo.day}</span>
                      {dayInfo.isClosed && <span className="text-xs">休</span>}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-4 flex items-center justify-center space-x-4 text-xs">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded mr-1.5" />
                <span className="text-gray-600">已選擇</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-gray-100 rounded mr-1.5" />
                <span className="text-gray-600">不可選</span>
              </div>
            </div>
          </div>

          {/* 時段選擇 */}
          {order.deliveryDate && (
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-3">
                選擇配送時段
              </label>
              <div className="space-y-3">
                {selectedDayData?.is_closed_all_day ? (
                  <div className="p-4 bg-gray-100 rounded-xl text-sm text-gray-600">
                    當日休息，無可預約時段
                  </div>
                ) : selectedDayData?.slots?.length ? (
                  selectedDayData.slots.map((slot) => {
                    const available = slot.available_capacity ?? 0;
                    const isPast = order.deliveryDate ? isSlotPastForToday(order.deliveryDate, slot.end_time) : false;
                    const isAvailable = available > 0 && !isPast;
                    const isSelected = order.deliverySlotId === slot.slot_id;
                    const label = `${slot.start_time} - ${slot.end_time}`;

                    return (
                      <button
                        key={slot.slot_id}
                        onClick={() =>
                          isAvailable &&
                          setOrder((prev) => ({
                            ...prev,
                            deliverySlotId: slot.slot_id,
                            deliverySlotSource: slot.slot_source,
                            deliverySlotLabel: label,
                          }))
                        }
                        disabled={!isAvailable}
                        className={`
                              w-full p-4 rounded-xl text-left transition-all
                              ${
                                isSelected
                                  ? "bg-blue-500 text-white shadow-lg"
                                  : ""
                              }
                              ${
                                isAvailable && !isSelected
                                  ? "bg-gray-50 hover:bg-blue-50 border-2 border-gray-200"
                                  : ""
                              }
                              ${
                                !isAvailable
                                  ? "bg-gray-100 opacity-50 cursor-not-allowed"
                                  : ""
                              }
                            `}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <MdAccessTime className="text-2xl" />
                            <span className="font-semibold">{label}</span>
                          </div>
                          <span
                            className={`text-sm ${
                              isSelected ? "text-white" : "text-gray-500"
                            }`}
                          >
                            {isAvailable ? `剩餘 ${available}` : isPast ? "已過時段" : "已額滿"}
                          </span>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="p-4 bg-gray-100 rounded-xl text-sm text-gray-600">
                    無可預約時段
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DeliverTimeCard;
